import { useContext, useEffect, useState } from "react";
import { FeatureFlagsContext } from "@/services/features/context";
import { FeatureFlags } from "@/services/features/types";

/**
 * One flag for the whole field redesign, so the rollout is a single switch.
 *
 * A missing provider is not an error, unlike `useFeatureFlag`: these controls
 * render in tests and stories outside the app shell, where "no flag source"
 * means "flag off". Reads false until the flag resolves, which shows the
 * pre-redesign rendering for the first tick rather than an empty row.
 */
export const useNewFieldsUI = (): boolean => {
  const featureFlags = useContext(FeatureFlagsContext);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Seeding false covers the first mount only. Every later re-evaluation has
    // to drop the previous answer too, or a provider that disappears or starts
    // rejecting leaves the redesign switched on for a session that can no
    // longer confirm the flag — the one direction this must never fail in.
    setEnabled(false);
    if (!featureFlags) {
      return;
    }
    let isCurrent = true;
    featureFlags
      .isEnabled(FeatureFlags.CRM_6980_PREVIEW_FIELDS)
      .then((value) => {
        if (isCurrent) {
          setEnabled(value);
        }
      })
      // A flag that cannot be read stays off: the field keeps the rendering it
      // had before the redesign rather than failing to render.
      .catch(() => {
        if (isCurrent) {
          setEnabled(false);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [featureFlags]);

  return enabled;
};
